/**
 * Recovery actions for 401 responses from the GraphQL API, used by the Apollo
 * error link.
 *
 * A 401 means the access token the request carried is expired or missing. Two
 * situations produce that: the token expired between Amplify's cached-session
 * check and the server's validation (recoverable — force a refresh and retry),
 * or the refresh token itself is dead, typically in a tab left open past the
 * session lifetime (unrecoverable — every poll would 401 forever, so sign the
 * user out and let the Authenticator show the login screen).
 */
import { fetchAuthSession, signOut } from "aws-amplify/auth"

/**
 * Outcome of a forced session refresh. Amplify resolves with no tokens only
 * for definitive authentication failures (it maps NotAuthorizedException to a
 * null token result and clears the store), and rethrows everything else —
 * network failures, throttling, service errors. So a resolved-but-empty
 * session means the session is dead, while a rejection is transient and must
 * NOT end the session.
 */
export type RefreshResult = "refreshed" | "session-expired" | "refresh-failed"

let refreshInFlight: Promise<RefreshResult> | null = null

/**
 * Force-refreshes the Cognito session and reports whether a usable access
 * token exists afterwards. Concurrent callers share one refresh — when a stale
 * tab wakes up, every in-flight poll 401s at once and must not each hit
 * Cognito with its own refresh request.
 */
export const refreshAccessToken = (): Promise<RefreshResult> => {
  if (!refreshInFlight) {
    refreshInFlight = fetchAuthSession({ forceRefresh: true })
      .then((session) => (session.tokens?.accessToken ? "refreshed" : "session-expired"))
      .catch(() => "refresh-failed" as const)
      .finally(() => {
        refreshInFlight = null
      })
  }
  return refreshInFlight
}

let signOutStarted = false

/**
 * Signs the user out so the Authenticator swaps the app for the login screen,
 * unmounting every polling query with it. Guarded so a burst of dead-session
 * 401s triggers a single sign-out.
 */
export const signOutExpiredSession = (): void => {
  if (signOutStarted) {
    return
  }
  signOutStarted = true
  signOut().catch(() => {
    signOutStarted = false
  })
}
